import React, { useCallback, useEffect, useState } from "react";
import {
  useAdvancedSearch,
  useGetAllDocumentsList,
  useGetAssessmentDocuments,
  useGetDocumentDetails,
  useGetProviderDocuments,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../custom-hooks/queries";
import ProviderDetailCard from "../components/ProviderDetailCard";
import CustomCards from "../components/CustomCards";
import Drawer from "../components/Drawer";
import FIltersDropdown from "../components/Filters/FiltersDropdown";
import Pagination from "../components/CustomPagination";
import CustomCardsSkeleton from "../components/CustomCardsSkeleton";
import Skeleton from "react-loading-skeleton";
import { useScreenWidth } from "../custom-hooks/useScreenWidth";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  setSearchText,
  setSearchTextByPage,
  setSearchTextDocs,
} from "../redux-store/features/providerFiltersSlice";
import Notification from "../components/Notification";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { getLocationFromProvider } from "../utils/addressUtils";

const UpdatedSearchBar = ({ onSearch }) => {
  const [currentPrompt, setCurrentPrompt] = useState("");

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default to avoid new line in textarea
      onSearch(currentPrompt);
    }
  };

  return (
    <div className="bg-white p-4 w-[100%]">
      <textarea
        className="w-[100%] border border-gray-400 p-2 rounded focus:outline-none focus:border-gray-500 transition-all duration-300 resize-none h-12 focus:h-32"
        placeholder="Enter your ChatDQP Prompt ..."
        value={currentPrompt}
        onChange={(e) => setCurrentPrompt(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export const Poc = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const screenWidth = useScreenWidth();
  const location = useLocation();
  const { data: providers } = useGetprovidersListForFilters();
  const searchQuery =
    useSelector((state) => state.providersFilters.searchTextByPage["Poc"]) ||
    "";
  const { data: rfis } = UseGetRfiListForFilters();

  const filters = [
    {
      id: "docType",
      name: "Document Type",
      options: [
        { docId: "RFI", label: "VQQ", checked: false },
        { docId: "VQA", label: "Audit Report", checked: false },
      ],
      total: 2,
    },
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },
    {
      id: "status",
      name: "Status",
      options: [
        { statusID: "Released", label: "Released", checked: false },
        { statusID: "Pending", label: "Pending", checked: false },
        { statusID: "Published", label: "Published", checked: false },
        { statusID: "Rejected", label: "Rejected", checked: false },
        { statusID: "Upcoming", label: "Upcoming", checked: false },
      ],
      total: 5,
    },
    {
      id: "services",
      name: "Services",
      options: rfis,
      total: 33,
    },
  ];

  useEffect(() => {
    if (isError) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, []); // Added dependency array to prevent continuous re-renders

  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "Poc",
          searchText: search,
        })
      );
    }, 300),
    [dispatch]
  );

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };

  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);

  const [selectedIds, setSelectedIds] = useState({
    docType: [],
    provider: [],
    status: [],
    services: [],
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds, searchQuery]);

  const { data, isLoading, isError, error } = useAdvancedSearch(
    currentPage,
    searchQuery
  );
  const {
    mutate,
    data: drawerData,
    isPending: drawerLoading,
  } = useGetDocumentDetails();

  const openDrawer = (documentId) => {
    mutate(documentId);
    dispatch(setDrawer(true));
  };

  const getDocLocation = (item, short = true) => {
    return getLocationFromProvider({
      address: item?.document_address || item?.document_location,
      city: item?.document_city,
      state: item?.document_state,
      country: item?.document_country
    }, { short, abbvState: true });
  };

  return (
    <div className="container px-4">
      {/* Header Section */}
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5">
        <h1 className="font-semibold">Vendor Discovery: ChatDQP AI Search</h1>
        <p className="text-sm">
          Query the list below using prompts to view a list of matching vendors.
          For example, "Give me a list of vendors who completed the Core VQQ
          template and that has Phase 2 Parkinsons experience."
        </p>
      </div>

      {/* Search Bar */}
      <div className="items-center mt-5 px-5">
        <div className="flex justify-between items-center">
          {/* 
            Choose one of the search bar options:
            - If you prefer using your existing MainSearchBar component, uncomment below
          */}
          {/*
          <MainSearchBar
            pageKey="Poc"
            placeholder="Query Vendors ..."
            height="40px"
            width={
              screenWidth <= 375
                ? "17.5rem"
                : screenWidth < 425
                ? "22rem"
                : "25rem"
            }
            marginRight={"mr-2"}
            isSearchIcon={true}
            onSearch={onSubmit}
          />
          */}
          {/* Or use the inline updated search bar */}
          <UpdatedSearchBar onSearch={onSubmit} />
          {/* You can also include the FiltersDropdown component if needed */}
          {/* <FIltersDropdown
            rfis={rfis}
            providers={providers}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
            filters={filters}
          /> */}
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <CustomCardsSkeleton isProviderDetail={true} />
      ) : (
        <>
          {data?.items.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center text-center space-y-2 min-h-96"
              style={{ marginTop: "100px" }}
            >
              <span className="p-3 rounded-full bg-custom-link-bg-color">
                <MagnifyingGlassIcon
                  width={20}
                  height={20}
                  style={{ color: "white" }}
                />
              </span>
              <p className="text-lg text-gray-700 font-semibold">
                No documents found
              </p>
              <p className="text-gray-500">
                Your search did not match any documents. Please try again.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center lg:justify-start 2xl:justify-start items-start mt-5 min-h-96">
              {data?.items?.map((item, index) => (
                <div
                  key={index}
                  className={`mb-4 md:${
                    index % 2 === 0 ? "ml-0" : "ml-10"
                  } md:ml-4`}
                >
                  <CustomCards
                    documentType={item?.entity_type}
                    documentName={item?.document_name}
                    entityId={item?.entity_id}
                    name={item?.provider_name}
                    status={item?.document_status}
                    services={item.capabilities?.map((capability) => ({
                      rfi_id: capability.rfi_id,
                      rfi_name: capability.rfi_name,
                    }))}
                    publishedDate={item.published_on}
                    requestedDate={item?.requested_on}
                    docLocation={getDocLocation(item)}
                    docLocationFull={getDocLocation(item, false)}
                    onClick={() => openDrawer(item?.document_id)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Drawer Component */}
      {isDrawerOpen && (
        <Drawer
          open={isDrawerOpen}
          docId={drawerData?.document_id}
          docType={drawerData?.document_type}
          docName={drawerData?.document_name}
          documents={drawerData}
          drawerLoading={drawerLoading}
        />
      )}

      {/* Pagination */}
      {!isLoading && data?.items.length > 0 && !isError && (
        <Pagination
          totalPages={data?.total_pages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error?.message}
        status={"error"}
      />
    </div>
  );
};
